'use client';

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAboveBarMaxHeight } from './chat/use-above-bar-max-height';

/**
 * How strongly the scrollback history is showing right now.
 *
 * - `hidden`     — unmounted; the resting panel is only the current question.
 * - `shown`      — full strength: the user just scrolled up, or is still working in it.
 * - `fading`     — depleting. Opacity eases 1 → 0 over HISTORY_FADE_MS while the block keeps
 *                  its full height, so NOTHING moves and the user's scroll position is left
 *                  exactly where they put it.
 * - `collapsing` — the short tail: opacity is already 0, so the height melts away over
 *                  HISTORY_COLLAPSE_MS with nothing visible moving, and the panel arrives at
 *                  its resting size without a snap.
 *
 * `fading` and `collapsing` are reversible states, not a teardown: any wheel, touch, tap or
 * focus inside the panel snaps back to `shown` in HISTORY_RESTORE_MS, interpolating from
 * wherever the animation had got to. The user can always catch it on the way out.
 *
 * Owned by AboveBarPromptPanel (it owns the gesture surface and the clock); AboveBarHistory
 * consumes it, runs the transition, and unmounts its rows only at `hidden`. Always `shown`
 * outside an autoCollapse panel so nothing else changes behavior.
 */
export type AboveBarRevealPhase = 'hidden' | 'shown' | 'fading' | 'collapsing';

const AboveBarRevealContext = createContext<AboveBarRevealPhase>('shown');
export function useAboveBarReveal() {
  return useContext(AboveBarRevealContext);
}

/** Full-strength hold after the last interaction, before the deplete starts. */
const HOLD_MS = 1500;
/** The slow deplete. Opacity only — see AboveBarRevealPhase. */
export const HISTORY_FADE_MS = 2400;
/** The invisible height collapse that follows it. */
export const HISTORY_COLLAPSE_MS = 280;
/** Catching it: fast enough to feel like the panel snapped back under the finger. */
export const HISTORY_RESTORE_MS = 150;
/** Clock granularity. Small enough that the deplete starts on time, cheap enough to ignore. */
const TICK_MS = 100;
/**
 * After the user touches something inside the panel, DOM churn is THEIRS — an editor opening,
 * a row swapping for its input — not streaming content. Re-pinning on it is what dragged the
 * view back down to the live question every time an upper row's Edit was clicked, so the pin
 * stands down for this long after any direct input.
 */
const INTERACTION_GRACE_MS = 700;

/**
 * Where the panel lives:
 * - `floating` — glass panel absolutely anchored `bottom-full` above the bar (legacy; still used
 *   by the marketing demo replica).
 * - `inline`   — glass panel in normal flow, e.g. at the top of the mobile search sheet.
 * - `card`     — the TOP REGION of the search capsule itself: no surface of its own (the capsule's
 *   border/background is the card), no nav clamp (the capsule sets `--hero-slot-max`), pointer
 *   events always on. Same pin/scroll behaviour as the other two.
 */
export type AboveBarPanelPlacement = 'floating' | 'inline' | 'card';

interface AboveBarPromptPanelProps {
  children: React.ReactNode;
  /** @deprecated use `placement="inline"` — kept for the mobile-sheet call sites. */
  inline?: boolean;
  placement?: AboveBarPanelPlacement;
  /**
   * Allow pointer events through to the panel (e.g. the parse-error retry button). Defaults to
   * pass-through so taps fall to the search bar underneath, matching the previous banner.
   * Scroll/wheel still reach children via `pointer-events-auto` on the glass surface so the
   * preamble can stay scrollable and peek on scroll-up after it dismisses. Ignored for `card`.
   */
  interactive?: boolean;
  /**
   * Opt-in idle deplete: after the user scrolls up (into history) and then goes
   * idle, drain back to the resting position so the panel reads as "just the
   * current question" again. Only the scrollback-history card placement sets this;
   * every other call site (budget sheet, region selector, discussion thread) is
   * unaffected.
   */
  autoCollapse?: boolean;
  /** Pause the deplete while true (a past-turn editor is open, or a regenerate is in flight). */
  suspendAutoCollapse?: boolean;
}

/**
 * Panel that holds the preamble/question banner (and the region / budget interstitials) above the
 * composer — either floating over the page or, in `card` placement, as the top region of the
 * search capsule.
 *
 * Bottom-anchored (`bottom-full`) so it grows UPWARD from the top edge of the search bar: the newest
 * line — the question — stays pinned at its resting height while the preamble and any wrapped lines
 * extend up and are clipped at the TOP instead of pushing down over the search bar. Bottom alignment
 * comes from an inner `mt-auto` wrapper, NOT `justify-content: flex-end`: with justify-end the
 * browser treats the scroller as end-aligned and snaps its scroll position to the bottom the moment
 * content first overflows the clamp — which yanked the question out of view and left only the answer
 * chips showing. `mt-auto` gives identical bottom alignment while content fits, but overflow scrolls
 * as a normal top-anchored scroller. A nav-aware max-height clamp keeps it from sliding under the
 * sticky top nav.
 *
 * Overflow is scrollable so a tall preamble+question pair stays reachable within the view, and so
 * scroll-up gestures can be handled by PromptBanner to briefly re-show a dismissed preamble.
 * When the question + chips already fit, the panel is not a scroller — otherwise overscroll
 * containment and the peek handler steal page scroll-up over the glass.
 *
 * Mirrors the discussion-thread floating panel (DiscussionThread `panel`) so the two read the same.
 * The `mb-*` resting offset keeps a single question line at roughly its previous (`-top-28`) height.
 */
export function AboveBarPromptPanel({
  children,
  interactive = false,
  inline = false,
  placement: placementProp,
  autoCollapse = false,
  suspendAutoCollapse = false,
}: AboveBarPromptPanelProps) {
  const placement: AboveBarPanelPlacement = placementProp ?? (inline ? 'inline' : 'floating');
  const isCard = placement === 'card';
  const panelRef = useRef<HTMLDivElement>(null);
  const maxHeight = useAboveBarMaxHeight(panelRef, placement === 'floating');
  // Assume overflow until measured so a tall first paint can still scroll.
  const [hasOverflow, setHasOverflow] = useState(true);
  // Scrollback visibility (autoCollapse panels only). See AboveBarRevealPhase.
  // Non-autoCollapse panels always provide `shown` so the context is inert everywhere else.
  const [revealPhase, setRevealPhase] = useState<AboveBarRevealPhase>('hidden');
  // Read inside the pin/deplete effect via refs so toggling an editor open/closed
  // (or the phase itself) doesn't tear down and recreate the observers.
  const suspendAutoCollapseRef = useRef(suspendAutoCollapse);
  useEffect(() => {
    suspendAutoCollapseRef.current = suspendAutoCollapse;
  }, [suspendAutoCollapse]);
  const revealPhaseRef = useRef(revealPhase);

  // Keep the panel pinned while content streams in (preamble typing, question
  // typing, chips revealing) so what matters is always the visible part; the
  // preamble stays reachable by scrolling up once the turn settles.
  //
  // The pin target is the `[data-pin-anchor]` element (PromptBanner marks the
  // question — or the preamble during a peek), not the container's absolute
  // bottom. Question + chips combined can be taller than `maxHeight` (it clamps
  // to as little as 120px) — bottom-pinning in that case scrolls the question
  // itself out of view and leaves only the chips showing, which reads as a
  // broken/empty panel until the user scrolls. So: bottom-pin, but never past
  // the anchor's top — only trailing chip rows (recoverable by scrolling down)
  // ever get clipped, never the question. Content with no anchor (price-tier /
  // region-selector interstitials) keeps plain bottom-pinning.
  //
  // Each pin re-asserts on the next animation frame: browser scroll adjustments
  // (scroll anchoring, overflow-appearing snap) can land AFTER the synchronous
  // MutationObserver pass and would otherwise silently override it.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    let raf = 0;
    let resizeRaf = 0;
    let cancelled = false;
    // Once the user scrolls this panel themselves, stop re-asserting the pin —
    // otherwise a later resize (page scroll tightening the clamp, chips wrapping)
    // yanks them back and feels like the panel stole the gesture.
    let userReleased = false;
    let lastInteractionAt = Date.now();
    // Last wheel/touch/tap/focus the user aimed AT this panel. Distinct from
    // lastInteractionAt (which the deplete clock also resets on its own): this one
    // only ever moves on real input, and it is what the pin stands down for.
    let lastDirectInputAt = 0;
    // While the user is driving — an editor open, or anything within a breath of
    // their last input — the DOM churn in here is theirs. Re-measure, never re-pin.
    const pinSuppressed = () =>
      suspendAutoCollapseRef.current || Date.now() - lastDirectInputAt < INTERACTION_GRACE_MS;
    const measure = () => {
      const maxScroll = el.scrollHeight - el.clientHeight;
      const overflows = maxScroll > 1;
      setHasOverflow((prev) => (prev === overflows ? prev : overflows));
      return { maxScroll, overflows };
    };
    const apply = () => {
      if (cancelled) return;
      const { maxScroll, overflows } = measure();
      if (userReleased || !overflows) return;
      const anchor = el.querySelector<HTMLElement>('[data-pin-anchor]');
      if (!anchor) {
        el.scrollTop = maxScroll;
        return;
      }
      const paddingTop = parseFloat(getComputedStyle(el).paddingTop) || 0;
      const anchorTop =
        anchor.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - paddingTop;
      el.scrollTop = Math.max(0, Math.min(maxScroll, anchorTop));
    };
    const pin = () => {
      apply();
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    const onUserScroll = () => {
      userReleased = true;
      lastInteractionAt = Date.now();
      lastDirectInputAt = lastInteractionAt;
    };
    // Taps and keyboard focus inside the panel (an Edit button, a chip) count as
    // activity WITHOUT flipping userReleased — that flag stays reserved for "the
    // user scrolled away from the pin", so a tap never changes streaming-pin
    // behavior. They do arm the pin grace above, and they do catch a deplete.
    const onUserActivity = () => {
      lastInteractionAt = Date.now();
      lastDirectInputAt = lastInteractionAt;
      const phase = revealPhaseRef.current;
      if (phase === 'fading' || phase === 'collapsing') showHistory();
    };
    pin();
    const mutations = new MutationObserver(() => {
      if (pinSuppressed()) {
        // The user opened an editor or swapped a row: their churn, their scroll.
        measure();
        return;
      }
      userReleased = false;
      pin();
    });
    mutations.observe(el, { childList: true, subtree: true, characterData: true });
    // Coalesced into a frame: the deplete resizes this element ~60x/s for a third
    // of a second, and a synchronous setState in a ResizeObserver callback is how
    // "ResizeObserver loop completed with undelivered notifications" starts.
    const resizes = new ResizeObserver(() => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        if (cancelled) return;
        if (pinSuppressed()) {
          measure();
          return;
        }
        apply();
      });
    });
    resizes.observe(el);
    el.addEventListener('wheel', onUserScroll, { passive: true });
    el.addEventListener('touchmove', onUserScroll, { passive: true });
    el.addEventListener('pointerdown', onUserActivity, { passive: true });
    el.addEventListener('focusin', onUserActivity);

    // Scrollback reveal + idle deplete (opt-in). A scroll-up gesture on the panel
    // reveals the history (which mounts ABOVE the pinned question, so continued
    // scrolling browses it); going idle drains it away again in two stages — see
    // AboveBarRevealPhase — leaving only the current question. The idle check is a
    // recurring interval, not a one-shot setTimeout: every tick re-derives its
    // state from Date.now(), so a delayed/dropped tick self-corrects on the next
    // one instead of silently never collapsing. Every reset scroll is the instant
    // pin, never scrollTo({behavior:'smooth'}) — a smooth scroll is a compositor
    // animation and can silently never finish when the page isn't being composited.
    let idleTimer: ReturnType<typeof setInterval> | undefined;
    let touchStartY: number | null = null;
    let phaseStartedAt = Date.now();
    const setPhase = (next: AboveBarRevealPhase) => {
      if (revealPhaseRef.current === next) return;
      revealPhaseRef.current = next;
      phaseStartedAt = Date.now();
      setRevealPhase(next);
      if (next === 'collapsing' || next === 'hidden') {
        // The block above the question is about to lose its height (invisibly —
        // opacity is already 0). Hold the question steady through it: the
        // ResizeObserver re-pins every frame of the collapse.
        userReleased = false;
        pin();
      }
    };
    function showHistory() {
      lastInteractionAt = Date.now();
      const phase = revealPhaseRef.current;
      if (phase === 'shown') return;
      setPhase('shown');
      // Mounting (hidden → shown) and un-collapsing both change the height of the
      // block ABOVE the question, so hold the question steady while it moves and
      // let the user's continued scroll-up walk into the history from there.
      // Catching it during `fading` changes no layout at all — leave their scroll
      // position exactly where they put it.
      if (phase === 'hidden' || phase === 'collapsing') {
        userReleased = false;
        pin();
      }
    }
    const onRevealWheel = (e: WheelEvent) => {
      // Scroll-up opens the history. Once it is on screen, movement in EITHER
      // direction means the user is still working in it and cancels the deplete.
      if (e.deltaY < 0 || revealPhaseRef.current !== 'hidden') showHistory();
    };
    const onRevealTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? null;
    };
    const onRevealTouchMove = (e: TouchEvent) => {
      if (revealPhaseRef.current !== 'hidden') {
        showHistory();
        return;
      }
      const y = e.touches[0]?.clientY;
      if (touchStartY == null || y == null) return;
      // Finger moving down = content scrolls up = reveal what lives above.
      if (y - touchStartY >= 24) {
        touchStartY = y;
        showHistory();
      }
    };
    if (autoCollapse) {
      el.addEventListener('wheel', onRevealWheel, { passive: true });
      el.addEventListener('touchstart', onRevealTouchStart, { passive: true });
      el.addEventListener('touchmove', onRevealTouchMove, { passive: true });
      idleTimer = setInterval(() => {
        if (cancelled) return;
        const phase = revealPhaseRef.current;
        if (phase === 'hidden') {
          // History already gone but the user left the panel scrolled (e.g.
          // browsing a long question + chips) — return to resting.
          if (userReleased && Date.now() - lastInteractionAt >= HOLD_MS) {
            userReleased = false;
            pin();
          }
          return;
        }
        if (suspendAutoCollapseRef.current) {
          // An editor is open, or a regenerate cascade is mid-flight: hold at full
          // strength and keep restarting the clock so nothing drains under them.
          lastInteractionAt = Date.now();
          setPhase('shown');
          return;
        }
        if (phase === 'shown') {
          if (Date.now() - lastInteractionAt >= HOLD_MS) setPhase('fading');
          return;
        }
        if (phase === 'fading') {
          if (Date.now() - phaseStartedAt >= HISTORY_FADE_MS) setPhase('collapsing');
          return;
        }
        if (Date.now() - phaseStartedAt >= HISTORY_COLLAPSE_MS) setPhase('hidden');
      }, TICK_MS);
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      cancelAnimationFrame(resizeRaf);
      mutations.disconnect();
      resizes.disconnect();
      el.removeEventListener('wheel', onUserScroll);
      el.removeEventListener('touchmove', onUserScroll);
      el.removeEventListener('pointerdown', onUserActivity);
      el.removeEventListener('focusin', onUserActivity);
      el.removeEventListener('wheel', onRevealWheel);
      el.removeEventListener('touchstart', onRevealTouchStart);
      el.removeEventListener('touchmove', onRevealTouchMove);
      if (idleTimer) clearInterval(idleTimer);
    };
  }, [autoCollapse]);

  const phaseValue: AboveBarRevealPhase = autoCollapse ? revealPhase : 'shown';

  if (isCard) {
    // Top region of the search card. Height is clamped by the capsule's
    // `--hero-slot-max` (space between the sticky nav and the composer on the
    // hero page; 42vh elsewhere). When the region overflows, the top edge fades
    // so clipped preamble/older lines read as "more above", not a hard cut.
    return (
      <div className="relative w-full">
        <div
          ref={panelRef}
          data-above-bar-panel
          style={{
            overflowAnchor: 'none',
            maxHeight: 'var(--hero-slot-max, 42vh)',
            ...(hasOverflow
              ? { maskImage: 'linear-gradient(to bottom, transparent, black 1.25rem)', WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 1.25rem)' }
              : null),
          }}
          className={`flex flex-col px-3.5 pb-3 pt-4 sm:px-4 sm:pb-3.5 sm:pt-5 ${
            hasOverflow ? 'soft-scrollbar overflow-y-auto overscroll-contain' : 'overflow-y-hidden'
          }`}
        >
          <div className="mt-auto flex w-full flex-col">
            <AboveBarRevealContext.Provider value={phaseValue}>{children}</AboveBarRevealContext.Provider>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        placement === 'inline'
          ? 'relative z-10 mb-3 w-full animate-fade-in-from-below'
          : 'absolute inset-x-0 bottom-full z-10 mb-10 max-sm:mb-6 animate-fade-in-from-below'
      }
      style={{ pointerEvents: interactive ? 'auto' : 'none' }}
    >
      <div
        ref={panelRef}
        data-above-bar-panel
        style={{
          // Chrome's scroll anchoring fights the explicit pin while content streams in.
          overflowAnchor: 'none',
          ...(maxHeight != null ? { maxHeight: `${maxHeight}px` } : null),
        }}
        className={`pointer-events-auto flex flex-col rounded-2xl bg-white/55 p-3.5 backdrop-blur-md dark:bg-dark/55 sm:p-4 ${
          hasOverflow ? 'soft-scrollbar overflow-y-auto overscroll-contain' : 'overflow-y-hidden'
        }`}
      >
        <div className="mt-auto flex w-full flex-col sm:min-h-40 justify-evenly">
          <AboveBarRevealContext.Provider value={phaseValue}>{children}</AboveBarRevealContext.Provider>
        </div>
      </div>
    </div>
  );
}
